import Service, { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { timeout, task } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import { groupBy } from 'lodash'
import { SearchGroup, SearchPager } from 'teacher-ui-client/book-library/book-finder/utils'
import type Store from '@ember-data/store'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type { PrimaryFilter, PrimaryFilterOption } from 'teacher-ui-client/reading-scales/utils'
import type { Log } from '@blakeelearning/log'
import type { BookClassification } from 'teacher-ui-client/models/book-library/book'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'

export interface StartSearchParams {
  primaryFilterFromValue: number
  primaryFilterToValue: number
  categories?: string[]
  withinCategories?: string[]
  currentPrimaryFilter: PrimaryFilter
  subscriptionType?: SubscriptionType
  keywords?: string
  excluded?: boolean
}

export interface BookSearchQueryFilter {
  searchKey: string
  keywords?: string
  withinCategories?: string
  categories?: string
  searchValue?: string
  subscriptionType?: SubscriptionType
  /**
   * true = only excluded books, false = no excluded books, undefined = all books
   */
  excluded?: boolean
}

interface BookSearchQuery {
  filter: BookSearchQueryFilter
  groupLimit: number
  groupOffset: number
  [more: string]: any
}

interface BookSearchResultsMeta {
  total: number
  groups: BookSearchResultsGroup[]
}

interface BookSearchResultsGroup {
  total: number
  classification: BookClassification
  search_value: number
}

function isBookSearchResultsMeta(meta: unknown): meta is BookSearchResultsMeta {
  return (
    meta !== null &&
    typeof meta === 'object' &&
    'total' in meta &&
    typeof meta.total === 'number' &&
    'groups' in meta &&
    Array.isArray(meta.groups)
  )
}

const fpPeaksSetToSetLabel: Record<string, string> = {
  '1': 'Set 1: s, a, t, p',
  '2': 'Set 2: m, i, d, n',
  '3': 'Set 3: g, o, c, k, ck',
  '4': 'Set 4: r, e, u',
  '5': 'Set 5: l, h, f, b, ll, ff, ss',
  '6': 'Set 6: j, v, w',
  '7': 'Set 7: x, y, z, zz, qu',
  '8': 'Set 8: sh, ch, th, ng',
  '9': 'Set 9: ai, ee, igh, oa',
  '10': 'Set 10: oo, ar, or, ur',
  '11': 'Set 11: ow, oi, ear, air',
  '12': 'Set 12: er, ure',
  '13': 'Set 13: CVCC, CCVC word',
  '14': 'Set 14: CCVCC, CCCVC words',
  '15': 'Set 15: ay, ie, ea, oy, ir',
  '16': 'Set 16: ou, ue, aw, wh, ph',
  '17': 'Set 17: ew, oe, au, ey',
  '18': 'Set 18: a-e, e-e, i-e, o-e, u-e',
  '19': 'Set 19: soft c, g, ow, ea, y, ey',
  '20': 'Set 20: tch, dge, st, gn, kn, wr, mb, lf, ear, ie',
}

export default class BookFinderService extends Service {
  @service store!: Store

  @service analytics!: AnalyticsService

  @service log!: Log

  /* Perform this when you want to start a new search based on some new filters
   *
   * It will send an initial request with a limit of 1, and use the results to determine how many books of each search
   * group & classification there are. With this, it creates a set of SearchGroups and SearchPagers, that the UI can use
   * to know what data to show and how to request more of it.
   *
   * Example:
   * [
      new SearchGroup('10L', [
        new SearchPager('fiction', { ...fictionFilters, searchValue: `5:7` }, nextPageTask),
        new SearchPager('nonfiction', { ...nonfictionFilters, searchValue: `5:7` }, nextPageTask),
      ]),
      new SearchGroup('20L', [
        new SearchPager('fiction', { ...fictionFilters, searchValue: `8:8` }, nextPageTask),
        new SearchPager('nonfiction', { ...nonfictionFilters, searchValue: `8:8` }, nextPageTask),
      ]),
      ...
    ]
   */
  startSearch = task(
    { restartable: true },
    waitFor(
      async ({
        currentPrimaryFilter,
        primaryFilterFromValue,
        primaryFilterToValue,
        categories,
        withinCategories,
        keywords,
        excluded,
        subscriptionType,
      }: StartSearchParams) => {
        await timeout(250) // debounce
        const filter: BookSearchQueryFilter = {
          searchKey: currentPrimaryFilter.searchKey,
          excluded,
        }
        if (isPresent(keywords)) filter.keywords = keywords
        if (withinCategories?.length) filter.withinCategories = withinCategories.join(',')
        if (isPresent(subscriptionType)) filter.subscriptionType = subscriptionType

        // treat empty categories like all categories
        const categoriesFlat = categories?.length ? categories : ['fiction.*', 'nonfiction.*']
        // now group the categories into their classifications
        const categoriesGrouped = groupBy(categoriesFlat, (cat) => cat.split('.')[0])

        // Get the array of options between the "from" and "to" values selected by the slider
        const scopedOptions = currentPrimaryFilter.options.slice(primaryFilterFromValue, primaryFilterToValue + 1)

        // Do initial recon request, to determine which search groups have results
        const reconFilter: BookSearchQueryFilter = {
          ...filter,
          categories: categoriesFlat.join(','), // all chosen categories
          searchValue: `${scopedOptions[0].searchMin}:${scopedOptions[scopedOptions.length - 1].searchMax}`, // all books in search scope
        }
        const bookSearchQuery: BookSearchQuery = {
          filter: reconFilter,
          groupLimit: 1,
          groupOffset: 0,
        }
        const booksResult = await this.store.query<BookLibraryBook>('book-library/book', bookSearchQuery)

        this.analytics.capture('Teacher Library - search entered', {
          ...reconFilter,
          primaryFilter: currentPrimaryFilter.id,
        })

        const groups = isBookSearchResultsMeta(booksResult.meta) ? booksResult.meta.groups : []

        // Don't want to know about empty group results - won't be creating a pager for these
        const groupsWithData = groups.filter((group) => group.total > 0)

        // Go from flat result groups array to grouping them by the search option they are part of
        // For example, blake reading level min:1 through max:7 might be represented by the search option book band "Purple"
        const groupedByOption = this.groupByOption(groupsWithData, scopedOptions)

        /* For each option group, return an initial SearchGroup. For each unique classification in it, return a SearchPager.
         * These contain details on how more books can be requested
         */
        const searchGroups = Object.entries(groupedByOption).map(([optionLabel, searchValueGroups]) => {
          const foundOption = scopedOptions.find((opt) => opt.label === optionLabel)
          if (!foundOption) {
            this.log.error('option not found for label', optionLabel)
          }
          const option = foundOption ?? scopedOptions[0]

          // Reduce search value groups down to just unique classifications that had data
          const classifications = Array.from(new Set(searchValueGroups.map((group) => group.classification)))

          // Each of those classifications becomes a pager, with a min and max search value indicated by the group it is in
          const searchPagers = classifications.map((classification: BookClassification) => {
            const classificationCategories = categoriesGrouped[classification]

            // Filter on just books for this classification & search value range for this group
            const pagerFilter: BookSearchQueryFilter = {
              ...filter,
              categories: classificationCategories.join(','),
              searchValue: `${option.searchMin}:${option.searchMax}`,
            }

            return new SearchPager(classification, pagerFilter, this.continueSearch)
          })

          // Want FP peaks to have different labels on the groups, but not on the slider labels, so make the change here
          if (currentPrimaryFilter.id === 'fp-peaks') {
            return new SearchGroup(fpPeaksSetToSetLabel[optionLabel] ?? optionLabel, searchPagers)
          }

          return new SearchGroup(optionLabel, searchPagers)
        })

        return searchGroups
      },
    ),
  )

  groupByOption(groups: BookSearchResultsGroup[], options: PrimaryFilterOption[]) {
    return groupBy(groups, (group) => {
      const searchValue = group.search_value
      const option = options.find((opt) => searchValue >= opt.searchMin && searchValue <= opt.searchMax)
      return option?.label
    })
  }

  // Perform to search for more books with a given filter and offset
  continueSearch = task(
    { enqueue: true },
    waitFor(async (filter: BookSearchQueryFilter, groupOffset: BookSearchQuery['groupOffset']) => {
      const groupLimit = 10

      const bookSearchQuery: BookSearchQuery = { filter, groupLimit, groupOffset }
      const booksResult = await this.store.query<BookLibraryBook>('book-library/book', bookSearchQuery)
      const books = booksResult.slice()
      const total = isBookSearchResultsMeta(booksResult.meta) ? booksResult.meta.total : 0
      return { books, total }
    }),
  )
}

export type ContinueSearchTask = BookFinderService['continueSearch']

declare module '@ember/service' {
  interface Registry {
    'book-library/book-finder': BookFinderService
  }
}
